import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Card, Table, Button } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUndo, faCheckDouble } from "@fortawesome/free-solid-svg-icons";
import { getListClassicSalesTicket } from "../../../../controller/api/__classic__/ticketStorage";
import { useCookies } from "react-cookie";
import { TableRow } from "../component/TableRow";
import SalesPairTicketRow from "../component/SalesPairTicketRow";
import { calculatePage } from "../../../../helper/helper";
import Pagination from "../../../../components/Pagination";
import { Context } from "../SalesTicketStorage";
import BulkReturnPopup from "../sub-screen/BulkReturnPopup";
import TicketDetailPopup from "../sub-screen/TicketDetailPopup";

const parseDateMs = (value) => {
  const ms = Date.parse(value || "");
  return Number.isFinite(ms) ? ms : 0;
};

const groupTickets = (tickets) => {
  if (!tickets || tickets.length === 0)
    return { singleTickets: [], pairGroups: [], rows: [] };

  const singleTickets = [];
  const pairMap = new Map();

  for (const ticket of tickets) {
    if (ticket.isSingle === false) {
      const key = ticket.last5Numbers;
      if (!pairMap.has(key)) {
        pairMap.set(key, []);
      }
      pairMap.get(key).push(ticket);
    } else {
      singleTickets.push(ticket);
    }
  }

  const pairGroups = Array.from(pairMap.entries()).map(
    ([last5Numbers, groupedTickets]) => ({
      last5Numbers,
      tickets: groupedTickets,
      representative: groupedTickets[0],
      totalTickets: groupedTickets.length,
    }),
  );

  const rows = [
    ...pairGroups.map((pairGroup) => ({
      type: "pairGroup",
      key: `pair-${pairGroup.last5Numbers}`,
      createdAtMs: Math.max(
        ...pairGroup.tickets.map((t) => parseDateMs(t.createdAt)),
      ),
      pairGroup,
    })),
    ...singleTickets.map((ticket) => ({
      type: "ticket",
      key: `ticket-${ticket.id}`,
      createdAtMs: parseDateMs(ticket.createdAt),
      ticket,
    })),
  ].sort(
    (a, b) =>
      b.createdAtMs - a.createdAtMs ||
      String(b.key).localeCompare(String(a.key)),
  );

  return { singleTickets, pairGroups, rows };
};

const _Table = ({ region }) => {
  const { state, dispatch } = useContext(Context);
  const [cookies] = useCookies(["lottmekong"]);
  const [data, setData] = useState({});
  const [page, setPage] = useState(0);
  const [bulkReturnPopupShow, setBulkReturnPopupShow] = useState(false);
  const [ticketDetailPopupShow, setTicketDetailPopupShow] = useState(false);
  const [selectedTicketDetail, setSelectedTicketDetail] = useState(null);

  // Multi-select state
  const [selectedTickets, setSelectedTickets] = useState(new Map()); // Map<id, ticket>

  const { singleTickets, pairGroups, rows } = useMemo(
    () => groupTickets(data.list),
    [data.list],
  );

  // Get all returnable tickets (single + from pair groups) - include partially returned
  const allReturnableTickets = useMemo(() => {
    const fromPairs = pairGroups.flatMap((g) =>
      g.tickets.filter(
        (t) =>
          Number(t.status) === 1 &&
          Number(t.amount) > 0 &&
          (!t.returnedAmount || t.returnedAmount < t.inputAmount),
      ),
    );
    const fromSingles = singleTickets.filter(
      (t) =>
        Number(t.status) === 1 &&
        Number(t.amount) > 0 &&
        (!t.returnedAmount || t.returnedAmount < t.inputAmount),
    );
    return [...fromPairs, ...fromSingles];
  }, [pairGroups, singleTickets]);

  const selectedTicketIds = useMemo(
    () => Array.from(selectedTickets.keys()),
    [selectedTickets],
  );
  const selectedTicketsList = useMemo(
    () => Array.from(selectedTickets.values()),
    [selectedTickets],
  );

  const getAllFunc = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const response = await getListClassicSalesTicket(
        cookies.lottmekong,
        {
          channel: state.channel,
          region: region,
          numbers: state.numbers,
          ticketCode: state.ticketCode,
          ticketType: state.ticketType || "all",
          fromDate: state.fromDate,
          toDate: state.toDate,
        },
        page,
      );

      if (response) {
        const res = {
          list: response.data,
          total: response.total,
          totalPage: calculatePage(response.total),
        };
        setData(res || {});
      }
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [
    cookies.lottmekong,
    dispatch,
    state.channel,
    state.numbers,
    state.ticketCode,
    state.ticketType,
    state.fromDate,
    state.toDate,
    region,
    page,
  ]);

  useEffect(() => {
    getAllFunc();
  }, [getAllFunc, state.filterPressCount]);

  // Clear selection when data changes
  useEffect(() => {
    setSelectedTickets(new Map());
  }, [data.list]);

  const handleSelectTicket = (ticketId, isSelected, ticketData) => {
    setSelectedTickets((prev) => {
      const next = new Map(prev);
      if (isSelected) {
        next.set(ticketId, ticketData);
      } else {
        next.delete(ticketId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedTickets.size === allReturnableTickets.length) {
      // Deselect all
      setSelectedTickets(new Map());
    } else {
      // Select all
      const newMap = new Map();
      allReturnableTickets.forEach((t) => newMap.set(t.id, t));
      setSelectedTickets(newMap);
    }
  };

  const handleShowDetail = (ticket) => {
    setSelectedTicketDetail(ticket);
    setTicketDetailPopupShow(true);
  };

  const handleBulkReturnSuccess = () => {
    setSelectedTickets(new Map());
    getAllFunc();
  };

  const allSelected =
    allReturnableTickets.length > 0 &&
    selectedTickets.size === allReturnableTickets.length;

  return (
    <>
      <Card border="light" className="shadow-sm mb-4 border-0">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table
              className="table-centered table-nowrap mb-0 rounded align-middle"
              hover
            >
              <thead className="bg-white border-bottom">
                <tr>
                  <th
                    className="border-bottom text-center py-3"
                    style={{ width: "50px" }}
                  >
                    <div className="form-check d-flex justify-content-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        style={{ cursor: "pointer", transform: "scale(1.2)" }}
                      />
                    </div>
                  </th>

                  <th
                    className="border-bottom py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    Mã Vé
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    Loại vé
                  </th>
                  <th
                    className="border-bottom text-start py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    Đài / Ngày xổ
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    Bộ số
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    SL Nhập
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    SL Bán
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    SL Tồn
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    SL Hoàn
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    Trạng thái bán
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    Trạng thái hoàn
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    Hình ảnh
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    Ngày tạo
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    Người nhập
                  </th>
                  <th
                    className="border-bottom text-center py-3 text-uppercase small fw-bold text-muted"
                    scope="col"
                  >
                    Người duyệt
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) =>
                  row.type === "pairGroup" ? (
                    <SalesPairTicketRow
                      key={row.key}
                      pairGroup={row.pairGroup}
                      showCheckbox={true}
                      selectedTicketIds={selectedTicketIds}
                      onSelectTicket={handleSelectTicket}
                      onShowDetail={handleShowDetail}
                    />
                  ) : (
                    <TableRow
                      key={row.key}
                      showCheckbox={true}
                      isSelected={selectedTickets.has(row.ticket.id)}
                      onSelect={(id, checked) =>
                        handleSelectTicket(id, checked, row.ticket)
                      }
                      onShowDetail={handleShowDetail}
                      {...row.ticket}
                    />
                  ),
                )}
                {(!data.list || data.list.length === 0) && (
                  <tr>
                    <td colSpan="15" className="text-center py-5 text-muted">
                      <div className="fs-5">Chưa có dữ liệu</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        <Card.Footer className="px-3 border-0 d-flex flex-column flex-lg-row align-items-center justify-content-between">
          <div className="fw-normal small mt-1 mt-lg-0 text-muted">
            Hiển thị <b>{data.list?.length || 0}</b> trong tổng{" "}
            <b>{data.total || 0}</b> vé
          </div>
          <Pagination
            totalPage={data.totalPage || 0}
            currentPage={page || 0}
            onPageChange={(i) => setPage(i)}
          />
        </Card.Footer>
      </Card>

      {/* Sticky action bar when tickets are selected */}
      {selectedTickets.size > 0 && (
        <div
          className="position-fixed bottom-0 start-0 end-0 bg-white shadow-lg border-top border-2 border-primary py-3 px-4"
          style={{
            zIndex: 1000,
            animation: "slideUp 0.3s ease-out",
          }}
        >
          <div className="container-fluid d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={faCheckDouble}
                className="text-primary fs-4"
              />
              <div>
                <div className="fw-bold text-primary fs-5">
                  Đã chọn {selectedTickets.size} vé
                </div>
                <div className="text-muted small">
                  Nhấn nút bên phải để hoàn trả các vé đã chọn
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={() => setSelectedTickets(new Map())}
              >
                Bỏ chọn
              </Button>
              <Button
                variant="danger"
                size="lg"
                onClick={() => setBulkReturnPopupShow(true)}
                className="px-4"
              >
                <FontAwesomeIcon icon={faUndo} className="me-2" />
                Hoàn {selectedTickets.size} vé
              </Button>
            </div>
          </div>
        </div>
      )}

      <BulkReturnPopup
        show={bulkReturnPopupShow}
        onHide={() => setBulkReturnPopupShow(false)}
        selectedTickets={selectedTicketsList}
        onSuccess={handleBulkReturnSuccess}
      />

      <TicketDetailPopup
        show={ticketDetailPopupShow}
        onHide={() => setTicketDetailPopupShow(false)}
        ticket={selectedTicketDetail}
        cookies={cookies}
        onRefresh={getAllFunc}
      />

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default React.memo(_Table);
